import React, { useState, useEffect } from 'react'
import {
	Box,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
	InputAdornment,
	Checkbox
} from '@mui/material'
import { supabase } from '../../utils/supabase'

// Clean phone number - remove all non-digit characters
const cleanPhoneNumber = (phone) => {
	if (!phone) return ''
	return phone.replace(/\D/g, '').slice(0, 10)
}

// Format phone number as user types: (xxx) xxx-xxxx
const formatPhoneWhileTyping = (digits) => {
	if (!digits) return ''
	const cleaned = digits.replace(/\D/g, '').slice(0, 10)

	if (cleaned.length === 0) return ''
	if (cleaned.length <= 3) return `(${cleaned}`
	if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`
	return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
}

// Phone Number Screen Component
const PhoneScreen = ({ phone, onPhoneChange, error, merchant, consent, onConsentChange, consentError }) => {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	const handlePhoneChange = (e) => {
		const value = cleanPhoneNumber(e.target.value)
		onPhoneChange(value)
	}

	return (
		<Box sx={{
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			gap: '32px',
			width: '100%'
		}}>
			{/* Merchant Name */}
			<Typography sx={{
				fontFamily: 'Inter',
				fontWeight: 600,
				fontSize: '34px',
				lineHeight: 1.29,
				color: '#2D3748',
				textAlign: 'center'
			}}>
				{merchant.name}
			</Typography>
			{/* Title */}
			<Typography sx={{
				fontFamily: 'Inter',
				fontWeight: 600,
				fontSize: isMobile ? '24px' : '28px',
				lineHeight: 1.29,
				color: '#2D3748',
				textAlign: 'center'
			}}>
				Enter your number to start booking!
			</Typography>

			{/* Phone Input */}
			<TextField
				value={formatPhoneWhileTyping(phone)}
				onChange={handlePhoneChange}
				placeholder="(xxx) xxx-xxxx"
				error={!!error}
				helperText={error}
				inputMode="tel"
				autoFocus
				sx={{
					width: isMobile ? '100%' : '326px',
					'& .MuiOutlinedInput-root': {
						borderRadius: '16px',
						bgcolor: '#FFFFFF',
						'& fieldset': {
							borderColor: '#CBD5E0'
						},
						'&:hover fieldset': {
							borderColor: '#9F7AEA'
						},
						'&.Mui-focused fieldset': {
							borderColor: '#9F7AEA'
						}
					},
					'& .MuiInputBase-input': {
						fontFamily: 'Inter',
						fontWeight: 400,
						fontSize: '16px',
						lineHeight: 2.25,
						color: '#2D3748',
						padding: '10px 24px',
						'&::placeholder': {
							color: '#A0AEC0',
							opacity: 1
						}
					}
				}}
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<Typography sx={{ color: '#2D3748', fontFamily: 'Inter' }}>+1</Typography>
						</InputAdornment>
					)
				}}
			/>

			{/* Consent Checkbox */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'flex-start',
					gap: '8px',
					paddingX: '16px',
					cursor: 'pointer'
				}}
				onClick={() => onConsentChange(!consent)}
			>
				<Checkbox
					checked={consent}
					onChange={(e) => onConsentChange(e.target.checked)}
					onClick={(e) => e.stopPropagation()}
					sx={{
						padding: 0,
						color: consentError ? '#E53E3E' : '#CBD5E0',
						'&.Mui-checked': {
							color: '#9F7AEA'
						}
					}}
				/>
				<Typography sx={{
					fontFamily: 'Inter',
					fontWeight: 400,
					fontSize: '14px',
					lineHeight: 1.4,
					color: consentError ? '#E53E3E' : '#4A5568',
					textAlign: 'left'
				}}>
					I agree to receive automated SMS messages from {merchant.name} related to appointment reminders and updates. Message frequency may vary. Message and data rates may apply. Reply STOP to opt out or HELP for help. View our <a href="/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy Policy</a>.
				</Typography>
			</Box>
		</Box>
	)
}

// Name and Email Screen Component
const NameEmailScreen = ({ name, lastName, email, onNameChange, onLastNameChange, onEmailChange, errors }) => {
	const inputStyles = {
		'& .MuiOutlinedInput-root': {
			borderRadius: '6px',
			bgcolor: '#FFFFFF',
			'& fieldset': {
				borderColor: '#CBD5E0'
			},
			'&:hover fieldset': {
				borderColor: '#9F7AEA'
			},
			'&.Mui-focused fieldset': {
				borderColor: '#9F7AEA'
			}
		},
		'& .MuiInputBase-input': {
			fontFamily: 'Inter',
			fontWeight: 400,
			fontSize: '16px', // 16px prevents iOS zoom on focus
			lineHeight: 1.21,
			color: '#2D3748',
			padding: '12px 16px',
			'&::placeholder': {
				color: '#A0AEC0',
				opacity: 1
			}
		}
	}

	const labelStyles = {
		fontFamily: 'Inter',
		fontWeight: 400,
		fontSize: '12px',
		lineHeight: 1.21,
		letterSpacing: '-2.08%',
		color: '#2D3748',
		mb: '4px'
	}

	return (
		<Box sx={{
			display: 'flex',
			flexDirection: 'column',
			gap: '22px',
			width: '100%'
		}}>
			{/* Card Container */}
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: '16px',
				padding: '16px',
				bgcolor: '#FFFFFF',
				borderRadius: '12px',
				boxShadow: '0px 4px 4px -4px rgba(12, 12, 13, 0.05), 0px 16px 32px -4px rgba(12, 12, 13, 0.1)'
			}}>
				{/* First Name and Last Name - Row */}
				<Box sx={{ display: 'flex', gap: '16px' }}>
					<Box sx={{ flex: 1 }}>
						<Typography sx={labelStyles}>First name</Typography>
						<TextField
							fullWidth
							value={name}
							onChange={(e) => onNameChange(e.target.value)}
							placeholder="First name"
							error={!!errors?.name}
							helperText={errors?.name}
							autoFocus
							sx={inputStyles}
						/>
					</Box>
					<Box sx={{ flex: 1 }}>
						<Typography sx={labelStyles}>Last name</Typography>
						<TextField
							fullWidth
							value={lastName}
							onChange={(e) => onLastNameChange(e.target.value)}
							placeholder="Last name"
							error={!!errors?.lastName}
							helperText={errors?.lastName}
							sx={inputStyles}
						/>
					</Box>
				</Box>

				{/* Email */}
				<Box>
					<Typography sx={labelStyles}>Email address</Typography>
					<TextField
						fullWidth
						type="email"
						value={email}
						onChange={(e) => onEmailChange(e.target.value)}
						placeholder="Enter email address"
						error={!!errors?.email}
						helperText={errors?.email}
						sx={inputStyles}
					/>
				</Box>
			</Box>
		</Box>
	)
}

// Main ClientInfoStep Component
function ClientInfoStep({
	clientInfo,
	onClientInfoChange,
	onExistingClientFound,
	onExistingPetsFound,
	merchant,
	subStep = 'phone', // 'phone' or 'name'
	onSubStepChange
}) {
	const [phoneError, setPhoneError] = useState('')
	const [nameErrors, setNameErrors] = useState({})
	const [checking, setChecking] = useState(false)
	const [consentError, setConsentError] = useState(false)

	// Use consent from clientInfo (controlled by parent)
	const consent = clientInfo.consent || false

	// Validate phone number (10 digits)
	const validatePhone = (phone) => {
		const cleaned = cleanPhoneNumber(phone)
		if (!cleaned) {
			return 'Phone number is required'
		}
		if (cleaned.length !== 10) {
			return 'Please enter a valid 10-digit phone number'
		}
		return ''
	}

	// Validate name and email
	const validateNameEmail = () => {
		const errors = {}
		if (!clientInfo.name?.trim()) {
			errors.name = 'First name is required'
		}
		if (!clientInfo.email?.trim()) {
			errors.email = 'Email is required'
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientInfo.email)) {
			errors.email = 'Please enter a valid email address'
		}
		return errors
	}

	// Check for existing client using both phone AND email
	const checkExistingClient = async (phone, email) => {
		if (!merchant?.id || !phone || !email) return null

		const formattedPhone = phone;
		const normalizedEmail = email.trim().toLowerCase()

		try {
			const { data, error } = await supabase
				.from('clients')
				.select('id, name, email, phone_number')
				.eq('merchant_id', merchant.id)
				.eq('phone_number', formattedPhone)
				.ilike('email', normalizedEmail)
				.maybeSingle()

			if (error) {
				console.error('Error checking existing client:', error)
				return null
			}

			return data
		} catch (err) {
			console.error('Error in checkExistingClient:', err)
			return null
		}
	}

	// Fetch pets for existing client
	const fetchClientPets = async (clientId) => {
		try {
			const { data, error } = await supabase
				.from('pets')
				.select('id, name, species, breed, weight')
				.eq('client_id', clientId)
				.order('name')

			if (error) {
				console.error('Error fetching client pets:', error)
				return []
			}

			return data || []
		} catch (err) {
			console.error('Error in fetchClientPets:', err)
			return []
		}
	}

	// Handle phone submit - just validate and move to next step
	const handlePhoneNext = () => {
		const error = validatePhone(clientInfo.phone)
		let hasError = false

		if (error) {
			setPhoneError(error)
			hasError = true
		} else {
			setPhoneError('')
		}

		if (!consent) {
			setConsentError(true)
			hasError = true
		} else {
			setConsentError(false)
		}

		if (hasError) {
			return false
		}

		onSubStepChange?.('name')
		return true
	}

	// Handle name/email submit - check for existing client using phone + email
	const handleNameNext = async () => {
		const errors = validateNameEmail()
		if (Object.keys(errors).length > 0) {
			setNameErrors(errors)
			return false
		}
		setNameErrors({})
		setChecking(true)

		try {
			// Check for existing client using both phone AND email
			const existingClient = await checkExistingClient(clientInfo.phone, clientInfo.email)

			if (existingClient) {
				// Found returning customer - link to existing client
				onClientInfoChange({
					...clientInfo,
					existingClientId: existingClient.id
				})
				onExistingClientFound?.(existingClient)

				// Fetch and pass pets to parent
				const pets = await fetchClientPets(existingClient.id)
				if (pets.length > 0) {
					onExistingPetsFound?.(pets)
				}
			} else {
				// New client - clear any existing client ID
				onClientInfoChange({
					...clientInfo,
					existingClientId: null
				})
			}

			return true
		} finally {
			setChecking(false)
		}
	}

	// Validate current step
	const validate = () => {
		if (subStep === 'phone') {
			return handlePhoneNext()
		} else {
			return handleNameNext()
		}
	}

	// Handle field changes
	const handlePhoneChange = (value) => {
		setPhoneError('')
		onClientInfoChange({ ...clientInfo, phone: value })
	}

	const handleConsentChange = (checked) => {
		onClientInfoChange({ ...clientInfo, consent: checked })
		if (checked) {
			setConsentError(false)
		}
	}

	const handleNameChange = (value) => {
		setNameErrors(prev => ({ ...prev, name: '' }))
		onClientInfoChange({ ...clientInfo, name: value })
	}

	const handleLastNameChange = (value) => {
		setNameErrors(prev => ({ ...prev, lastName: '' }))
		onClientInfoChange({ ...clientInfo, lastName: value })
	}

	const handleEmailChange = (value) => {
		setNameErrors(prev => ({ ...prev, email: '' }))
		onClientInfoChange({ ...clientInfo, email: value })
	}

	// Expose validate function to parent via ref or callback
	useEffect(() => {
		// This allows parent to call validate when Next is clicked
		if (onClientInfoChange) {
			onClientInfoChange({ ...clientInfo, _validate: validate })
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [subStep, clientInfo.phone, clientInfo.name, clientInfo.lastName, clientInfo.email, clientInfo.consent])

	if (subStep === 'phone') {
		return (
			<PhoneScreen
				merchant={merchant}
				phone={clientInfo.phone}
				onPhoneChange={handlePhoneChange}
				error={phoneError}
				consent={consent}
				onConsentChange={handleConsentChange}
				consentError={consentError}
			/>
		)
	}

	return (
		<NameEmailScreen
			name={clientInfo.name}
			lastName={clientInfo.lastName}
			email={clientInfo.email}
			onNameChange={handleNameChange}
			onLastNameChange={handleLastNameChange}
			onEmailChange={handleEmailChange}
			errors={nameErrors}
		/>
	)
}

export default ClientInfoStep

